import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { X, Search, UserPlus } from 'lucide-react';
import { Button, Input } from '@/components/ui';
import { useNotification } from '@/hooks/useNotification';
import { userService } from '@/services/userService';
import { locationUserService } from '../../services/locationUserService';
import { PROGRAM_OPTIONS } from '../../utils/constants';
import type { User, UserListParams } from '@/types/user';
import type { LocationUserInsertItem } from '../../types/locationUser';

interface AddUsersModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const PAGE_SIZE = 10;

/** Map a User from the MIS API to the insert format expected by the Supabase endpoint. */
const mapToInsertItem = (user: User): LocationUserInsertItem => ({
  fusion_auth_user_id: user.fusion_auth_user_id,
  first_name: user.first_name,
  last_name: user.last_name ?? undefined,
  email: user.email,
  phone_number: user.phone_number,
  permanent_address: user.permanent_address,
  employee_id: user.employee_id,
  roles: user.roles,
  gender: user.gender,
  status: user.status,
  joining_date: user.joining_date || undefined,
  district: user.district ?? undefined,
});

export const AddUsersModal: React.FC<AddUsersModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedProgram, setSelectedProgram] = useState('');
  const [selectedUsers, setSelectedUsers] = useState<Map<string, User>>(new Map());
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { showSuccess, showError } = useNotification();

  // Reset state when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setSearchQuery('');
      setCurrentPage(1);
      setSelectedProgram('');
      setSelectedUsers(new Map());
      setIsSubmitting(false);
    }
  }, [isOpen]);

  // Build MIS user list filters
  const filters: UserListParams = {
    page: currentPage,
    page_size: PAGE_SIZE,
    q: searchQuery?.trim() || undefined,
    program: selectedProgram || undefined,
    status: 'Active',
  };

  // Fetch MIS users
  const {
    data: usersData,
    isLoading,
  } = useQuery({
    queryKey: ['mis-users-for-location', filters],
    queryFn: () => userService.getUsers(filters),
    enabled: isOpen,
    placeholderData: (prev) => prev,
  });

  const users: User[] = usersData?.list ?? [];
  const totalUsers = usersData?.total ?? 0;
  const totalPages = Math.ceil(totalUsers / PAGE_SIZE);

  // --- Selection ---
  const toggleUser = (user: User) => {
    const next = new Map(selectedUsers);
    const key = user.fusion_auth_user_id;
    if (next.has(key)) {
      next.delete(key);
    } else {
      next.set(key, user);
    }
    setSelectedUsers(next);
  };

  const isUserSelected = (user: User) => selectedUsers.has(user.fusion_auth_user_id);

  const allOnPageSelected =
    users.length > 0 && users.every((u) => selectedUsers.has(u.fusion_auth_user_id));

  const toggleSelectAllOnPage = () => {
    const next = new Map(selectedUsers);
    if (allOnPageSelected) {
      users.forEach((u) => next.delete(u.fusion_auth_user_id));
    } else {
      users.forEach((u) => next.set(u.fusion_auth_user_id, u));
    }
    setSelectedUsers(next);
  };

  // --- Submit ---
  const handleSubmit = async () => {
    if (selectedUsers.size === 0) {
      showError('No Users Selected', 'Please select at least one user to add.');
      return;
    }

    setIsSubmitting(true);
    try {
      const insertItems = Array.from(selectedUsers.values()).map(mapToInsertItem);
      const response = await locationUserService.insertUsers(insertItems);
      showSuccess(
        'Users Added',
        response.message || `${response.count} user(s) added to location tracking.`
      );
      onSuccess();
    } catch (err: any) {
      const message =
        err?.response?.data?.message || err?.message || 'Failed to add users.';
      showError('Insert Failed', message);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-rgf-gray-200">
          <div>
            <h2 className="text-lg font-semibold text-rgf-gray-900">
              Add Users to Location Tracking
            </h2>
            <p className="text-sm text-rgf-gray-500">
              Select users from the system to enable for location tracking.
            </p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            icon={<X className="h-4 w-4" />}
            onClick={onClose}
            disabled={isSubmitting}
          />
        </div>

        {/* Search & Filters */}
        <div className="px-6 py-4 border-b border-rgf-gray-200 space-y-3">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1">
              <Input
                placeholder="Search by name, email, or phone..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(1);
                }}
                startIcon={<Search className="h-4 w-4" />}
              />
            </div>
            <select
              value={selectedProgram}
              onChange={(e) => {
                setSelectedProgram(e.target.value);
                setCurrentPage(1);
              }}
              className="px-3 py-2 border border-rgf-gray-300 rounded-lg focus:ring-2 focus:ring-rgf-primary-500 focus:border-rgf-primary-500"
            >
              <option value="">All Programs</option>
              {PROGRAM_OPTIONS.map((p) => (
                <option key={p.value} value={p.value}>
                  {p.label}
                </option>
              ))}
            </select>
          </div>
          {selectedUsers.size > 0 && (
            <div className="text-sm text-blue-700 bg-blue-50 px-3 py-2 rounded-lg">
              {selectedUsers.size} user(s) selected for insertion
            </div>
          )}
        </div>

        {/* Table */}
        <div className="flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="p-8 text-center">
              <div className="w-8 h-8 border-2 border-rgf-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-rgf-gray-600">Loading users...</p>
            </div>
          ) : users.length === 0 ? (
            <div className="p-8 text-center">
              <p className="text-rgf-gray-600">No users found.</p>
            </div>
          ) : (
            <table className="min-w-full divide-y divide-rgf-gray-200">
              <thead className="bg-rgf-gray-50 sticky top-0">
                <tr>
                  <th className="table-header w-10">
                    <input
                      type="checkbox"
                      checked={allOnPageSelected}
                      onChange={toggleSelectAllOnPage}
                      className="rounded border-rgf-gray-300 text-rgf-primary-600 focus:ring-rgf-primary-500"
                    />
                  </th>
                  <th className="table-header">Name</th>
                  <th className="table-header">Email</th>
                  <th className="table-header">Employee ID</th>
                  <th className="table-header">Roles</th>
                  <th className="table-header">Status</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-rgf-gray-200">
                {users.map((user) => (
                  <tr
                    key={user.db_id}
                    className={`hover:bg-rgf-gray-50 cursor-pointer ${
                      isUserSelected(user) ? 'bg-blue-50' : ''
                    }`}
                    onClick={() => toggleUser(user)}
                  >
                    <td className="table-cell">
                      <input
                        type="checkbox"
                        checked={isUserSelected(user)}
                        onChange={() => toggleUser(user)}
                        onClick={(e) => e.stopPropagation()}
                        className="rounded border-rgf-gray-300 text-rgf-primary-600 focus:ring-rgf-primary-500"
                      />
                    </td>
                    <td className="table-cell">
                      <div className="flex items-center">
                        <div className="h-8 w-8 bg-rgf-primary-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <span className="text-xs font-medium text-rgf-primary-700">
                            {user.first_name?.[0]}
                            {user.last_name?.[0]}
                          </span>
                        </div>
                        <div className="ml-3">
                          <div className="text-sm font-medium text-rgf-gray-900">
                            {user.first_name} {user.last_name}
                          </div>
                          <div className="text-xs text-rgf-gray-500">
                            {user.district}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="table-cell">
                      <div className="text-sm text-rgf-gray-900">{user.email}</div>
                    </td>
                    <td className="table-cell">
                      <div className="text-sm text-rgf-gray-900">
                        {user.employee_id}
                      </div>
                    </td>
                    <td className="table-cell">
                      <div className="flex flex-wrap gap-1">
                        {user.roles.slice(0, 2).map((role, i) => (
                          <span key={i} className="badge badge-info text-xs">
                            {role}
                          </span>
                        ))}
                        {user.roles.length > 2 && (
                          <span className="text-xs text-rgf-gray-500">
                            +{user.roles.length - 2} more
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="table-cell">
                      <span
                        className={`badge ${
                          user.status === 'Active' ? 'badge-success' : 'badge-error'
                        }`}
                      >
                        {user.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        {totalUsers > 0 && (
          <div className="px-6 py-2 border-t border-rgf-gray-200 bg-rgf-gray-50">
            <div className="flex items-center justify-between">
              <div className="text-sm text-rgf-gray-700">
                Showing {(currentPage - 1) * PAGE_SIZE + 1} to{' '}
                {Math.min(currentPage * PAGE_SIZE, totalUsers)} of {totalUsers}
              </div>
              <div className="flex items-center space-x-2">
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={currentPage <= 1}
                  onClick={() => setCurrentPage(currentPage - 1)}
                >
                  Previous
                </Button>
                <span className="text-sm text-rgf-gray-700">Page {currentPage}</span>
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={currentPage >= totalPages}
                  onClick={() => setCurrentPage(currentPage + 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-rgf-gray-200">
          <Button variant="secondary" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            icon={<UserPlus className="h-4 w-4" />}
            onClick={handleSubmit}
            disabled={isSubmitting || selectedUsers.size === 0}
          >
            {isSubmitting
              ? 'Adding Users...'
              : `Add ${selectedUsers.size} User(s)`}
          </Button>
        </div>
      </div>
    </div>
  );
};
